import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import { useAuth } from '../hooks/useAuth'
import { apiClient, MatchClaimData } from '../services/apiClient'

export default function MatchClaimPage() {
  const { matchId } = useParams<{ matchId: string }>()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  const [matchData, setMatchData] = useState<MatchClaimData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const [claiming, setClaiming] = useState(false)
  const [selectedScoreId, setSelectedScoreId] = useState<number | null>(null)

  useEffect(() => {
    if (!matchId) {
      setError('Invalid match ID')
      setLoading(false)
      return
    }

    const fetchMatchData = async () => {
      try {
        setLoading(true)
        const data = await apiClient.getMatchClaimData(matchId)
        setMatchData(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load match data')
      } finally {
        setLoading(false)
      }
    }

    fetchMatchData()
  }, [matchId])

  const handleClaimMatch = async () => {
    if (!matchId || !selectedScoreId) return

    try {
      setClaiming(true)
      await apiClient.claimMatch(matchId, { scoreId: selectedScoreId })
      // Redirect immediately upon successful claim
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to claim match')
      setClaiming(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="flex items-center justify-center py-24">
          <div className="text-firedup-orange text-xl">Loading match data...</div>
        </div>
      </div>
    )
  }

  if (error || !matchData) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="flex flex-col items-center justify-center py-24">
          <div className="text-red-400 text-xl mb-4">{error || 'Match not found'}</div>
          <Link to="/" className="btn-primary">
            Return Home
          </Link>
        </div>
      </div>
    )
  }

  // Debug logging
  console.log('Match data:', matchData);
  console.log('Match claimed status:', matchData.claimed);
  if (matchData.scores && Array.isArray(matchData.scores)) {
    console.log('Individual scores:', matchData.scores.map(score => {
      const scoreData = typeof score.data === 'string' ? JSON.parse(score.data) : score.data;
      return {
        scoreId: score.scoreId,
        playerName: score.playerName,
        claimed: scoreData?.claimed,
        canClaim: scoreData?.canClaim,
        claimedBy: scoreData?.claimedBy
      };
    }));
  } else {
    console.log('No scores array found:', matchData.scores);
  }

  // Ensure scores array exists
  const scores = matchData.scores || [];
  console.log('Using scores array with length:', scores.length);

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">
            Claim Your Score
          </h1>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Match Information */}
          <div className="bg-firedup-gray/50 backdrop-blur-sm rounded-lg border border-firedup-light-gray p-6 mb-6">
            <h2 className="text-2xl font-bold text-white mb-4">Match Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <h3 className="text-firedup-orange font-semibold mb-2">Match Info</h3>
                <p className="text-white"><strong>Game:</strong> {matchData.title?.name || 'Unknown Game'}</p>
                <p className="text-white"><strong>Duration:</strong> {formatDuration(matchData.match?.durationSeconds || 0)}</p>
                {matchData.match?.location && (
                  <p className="text-gray-300"><strong>Location:</strong> {matchData.match.location.name}</p>
                )}
                {matchData.match?.utcTimestamp && (
                  <p className="text-gray-300"><strong>Played:</strong> {formatDate(matchData.match.utcTimestamp)}</p>
                )}
              </div>
              
              {!isAuthenticated && (
                <div className="flex flex-col justify-center items-center text-center">
                  <p className="text-lg text-gray-300 mb-4">
                    You must be logged in to claim a score
                  </p>
                  <Link to={`/login?redirect=${encodeURIComponent(location.pathname)}`} className="btn-primary">
                    Sign In to Claim
                  </Link>
                </div>
              )}
            </div>

            {matchData.claimed && (
              <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 mb-6">
                <p className="text-green-300">
                  <strong>✅ All scores in this match have been claimed</strong>
                </p>
              </div>
            )}
          </div>

          {/* Match Scores */}
          <div className="bg-firedup-gray/50 backdrop-blur-sm rounded-lg border border-firedup-light-gray p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Match Scores</h2>
            
            {!isAuthenticated ? (
              <div>
                {/* Show scores for unauthenticated users (read-only) */}
                <div className="space-y-3">
                  {scores.map((score, index) => {
                    const scoreData = typeof score.data === 'string' ? JSON.parse(score.data) : score.data;
                    const isClaimed = scoreData?.claimed || false;
                    const claimedBy = scoreData?.claimedBy;
                    
                    return (
                      <div
                        key={score.scoreId || index}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          isClaimed 
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-firedup-light-gray bg-firedup-gray/20'
                        }`}
                      >
                        <div className="flex items-center space-x-4">
                          {isClaimed ? (
                            <div className="h-4 w-4 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          ) : (
                            <div className="h-4 w-4 bg-firedup-orange rounded-full opacity-50"></div>
                          )}
                          <div>
                            <p className={`font-semibold ${isClaimed ? 'text-green-300' : 'text-white'}`}>
                              {isClaimed && claimedBy ? claimedBy : score.playerName}
                            </p>
                            <p className="text-gray-400 text-sm">
                              {score.location}
                              {isClaimed ? (
                                <span className="ml-2 text-green-400">(Claimed)</span>
                              ) : (
                                <span className="ml-2 text-firedup-orange">(Available)</span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className={`font-bold text-xl ${isClaimed ? 'text-green-300' : 'text-firedup-orange'}`}>
                            {score.score.toLocaleString()}
                          </p>
                          <p className="text-gray-400 text-sm">{formatDate(score.date)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : matchData.claimed ? (
              <div className="text-center py-4">
                <p className="text-gray-300 mb-4">All scores in this match have been claimed</p>
                <Link to="/dashboard" className="btn-secondary">
                  Go to Dashboard
                </Link>
              </div>
            ) : (
              <div>
                {/* Show warning only if user cannot claim any scores (meaning they already claimed one) */}
                {!scores.some(score => {
                  const scoreData = typeof score.data === 'string' ? JSON.parse(score.data) : score.data;
                  return scoreData?.canClaim === true;
                }) ? (
                  <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 mb-6">
                    <p className="text-yellow-300">
                      <strong>⚠️ You have already claimed a score in this match</strong>
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-300 mb-6">
                    Select the player score that belongs to you to claim this match:
                  </p>
                )}
                
                <div className="space-y-3">
                  {scores.map((score, index) => {
                    const scoreData = typeof score.data === 'string' ? JSON.parse(score.data) : score.data;
                    const isClaimed = scoreData?.claimed || false;
                    const canClaim = scoreData?.canClaim || false;
                    const claimedBy = scoreData?.claimedBy;
                    
                    return (
                      <div
                        key={score.scoreId || index}
                        className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${
                          isClaimed 
                            ? 'border-firedup-orange bg-firedup-orange/10'
                            : canClaim
                              ? selectedScoreId === score.scoreId
                                ? 'border-green-500 bg-green-500/10 cursor-pointer'
                                : 'border-firedup-light-gray hover:border-green-500/50 cursor-pointer'
                              : 'border-gray-600 bg-gray-600/10 opacity-50'
                        }`}
                        onClick={() => {
                          console.log('Score row clicked, canClaim:', canClaim, 'scoreId:', score.scoreId);
                          if (canClaim && score.scoreId) {
                            console.log('Setting selectedScoreId to:', score.scoreId);
                            setSelectedScoreId(score.scoreId);
                          }
                        }}
                      >
                        <div className="flex items-center space-x-4">
                          {isClaimed ? (
                            <div className="h-4 w-4 bg-firedup-orange rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✗</span>
                            </div>
                          ) : canClaim ? (
                            <div className="relative">
                              <input
                                type="radio"
                                id={`score-${score.scoreId || index}`}
                                name="selectedScore"
                                value={score.scoreId || ''}
                                checked={selectedScoreId === score.scoreId}
                                onChange={(e) => {
                                  const scoreId = parseInt(e.target.value);
                                  if (!isNaN(scoreId)) {
                                    setSelectedScoreId(scoreId);
                                  }
                                }}
                                className="sr-only"
                              />
                              <div 
                                className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                  selectedScoreId === score.scoreId
                                    ? 'border-green-500 bg-green-500'
                                    : 'border-firedup-light-gray bg-transparent'
                                }`}
                              >
                                {selectedScoreId === score.scoreId && (
                                  <span className="text-white text-xs">✓</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="h-4 w-4 bg-gray-600 rounded-full"></div>
                          )}
                          <div>
                            <p className={`font-semibold ${
                              isClaimed ? 'text-firedup-orange' : 
                              selectedScoreId === score.scoreId ? 'text-green-300' : 'text-white'
                            }`}>
                              {isClaimed && claimedBy ? claimedBy : score.playerName}
                            </p>
                            <p className="text-gray-400 text-sm">
                              {score.location}
                              {isClaimed ? (
                                <span className="ml-2 text-firedup-orange">(Claimed)</span>
                              ) : selectedScoreId === score.scoreId ? (
                                <span className="ml-2 text-green-400">(Selected)</span>
                              ) : (
                                <span className="ml-2 text-gray-400">(Available)</span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className={`font-bold text-xl ${
                            isClaimed ? 'text-firedup-orange' : 
                            selectedScoreId === score.scoreId ? 'text-green-300' : 'text-firedup-orange'
                          }`}>
                            {score.score.toLocaleString()}
                          </p>
                          <p className="text-gray-400 text-sm">{formatDate(score.date)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Only show claim button if user can claim and hasn't already claimed */}
                {scores.some(score => {
                  const scoreData = typeof score.data === 'string' ? JSON.parse(score.data) : score.data;
                  return scoreData?.canClaim;
                }) && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={handleClaimMatch}
                      disabled={!selectedScoreId || claiming}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {claiming ? 'Claiming Match...' : 'Claim This Score'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
